import React from "react";
import { ChatBubbleBottomCenterTextIcon } from "@heroicons/react/24/outline";
import { cleanHtml } from "./activityFormatters";

/**
 * Formats and renders HTML content safely with rich typography, list support, and dark-mode adaptation.
 */
export function FormattedHtmlContent({ content, className = "" }) {
  if (!content) return <span className="text-gray-400 dark:text-dark-500 italic">-</span>;

  const containsHtml = /<[a-z][\s\S]*>/i.test(content);

  if (containsHtml) {
    const cleaned =  cleanHtml(content);
    return (
      <div
        className={`prose prose-xs sm:prose-sm dark:prose-invert max-w-none leading-relaxed font-normal break-words [&>p]:mb-1.5 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5 [&>h1]:text-base [&>h1]:font-bold [&>h2]:text-sm [&>h2]:font-bold [&>h3]:text-xs [&>h3]:font-semibold [&>blockquote]:border-l-2 [&>blockquote]:border-indigo-400 [&>blockquote]:pl-2.5 [&>blockquote]:italic ${className}`}
        dangerouslySetInnerHTML={{ __html: cleaned }}
      />
    );
  }

  return (
    <div className={`whitespace-pre-wrap break-words leading-relaxed ${className}`}>
      {content}
    </div>
  );
}

/**
 * Dedicated visual presentation for Client Note activity logs.
 * Shows only the updated Client Wish cleanly formatted without previous version clutter.
 */
export function ClientNoteDetailsView({ parsedClientNote, defaultRemarks }) {
  const { type, category, diffs, clientWish, noChanges } = parsedClientNote;

  return (
    <div className="space-y-2.5 mt-1.5 text-xs">
      {/* Badges for Note Type & Category */}
      {(type || category) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {type && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-blue-50/90 text-blue-700 border border-blue-200/60 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/50 shadow-sm shadow-blue-500/5">
              <span className="text-blue-500/70 dark:text-blue-400/70 font-semibold text-[10px] uppercase tracking-wider">Type:</span>
              <span className="font-semibold">{type}</span>
            </span>
          )}
          {category && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-purple-50/90 text-purple-700 border border-purple-200/60 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-900/50 shadow-sm shadow-purple-500/5">
              <span className="text-purple-500/70 dark:text-purple-400/70 font-semibold text-[10px] uppercase tracking-wider">Category:</span>
              <span className="font-semibold">{category}</span>
            </span>
          )}
        </div>
      )}

      {/* Field Diffs (Cost, Assigned To, Statuses, etc.) */}
      {diffs && diffs.length > 0 && (
        <div className="space-y-2 rounded-xl border border-gray-200/70 bg-gray-50/60 p-3 dark:border-dark-700/70 dark:bg-dark-800/60 shadow-sm">
          <span className="font-semibold text-gray-600 dark:text-dark-300 block mb-1 text-[11px] uppercase tracking-wide">
            {diffs.length} field{diffs.length > 1 ? "s" : ""} updated:
          </span>
          <div className="space-y-1.5">
            {diffs.map((d, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-bold text-gray-700 dark:text-dark-200 min-w-[90px]">
                  {d.field}:
                </span>
                {d.old && d.old !== "—" ? (
                  <span className="rounded bg-rose-50 px-2 py-0.5 text-rose-600 line-through dark:bg-rose-955/30 dark:text-rose-400 border border-rose-200/50 dark:border-rose-900/30 font-medium">
                    {d.old}
                  </span>
                ) : (
                  <span className="italic text-gray-400 dark:text-dark-500">(empty)</span>
                )}
                <span className="font-bold text-gray-400">→</span>
                {d.new && d.new !== "—" ? (
                  <span className="rounded bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 dark:bg-emerald-955/30 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-900/30">
                    {d.new}
                  </span>
                ) : (
                  <span className="italic text-gray-400 dark:text-dark-500">(cleared)</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client Wish - Updated Wish Only */}
      {clientWish && (
        <div className="space-y-1.5 pt-0.5">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-dark-200">
            <ChatBubbleBottomCenterTextIcon className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
            <span>Client Wish</span>
          </div>

          {clientWish.hasNew ? (
            <div className="p-3 rounded-xl bg-slate-50/60 dark:bg-dark-800/50 border border-slate-150 dark:border-dark-750">
              <FormattedHtmlContent
                content={clientWish.newWish}
                className="text-slate-800 dark:text-slate-100 text-xs"
              />
            </div>
          ) : (
            <div className="p-2.5 rounded-xl border border-dashed border-gray-200/80 dark:border-dark-700/80 bg-gray-50/50 dark:bg-dark-800/30 text-xs text-gray-500 dark:text-dark-400 italic">
              (Wish cleared)
            </div>
          )}
        </div>
      )}

      {/* No changes indicator if saved with no edits */}
      {noChanges && !clientWish && (!diffs || diffs.length === 0) && (
        <p className="text-xs italic text-gray-500 dark:text-dark-400">
          No fields changed
        </p>
      )}

      {/* Remarks or Notes if present */}
      {(clientWish?.remarks || defaultRemarks) && (
        <p className="mt-1 text-xs text-gray-600 italic dark:text-gray-400 break-words">
          Note: {clientWish?.remarks || defaultRemarks}
        </p>
      )}
    </div>
  );
}
